/**
 * Tagged template literal for CSS that:
 * - Removes extra whitespace
 * - Validates CSS syntax in development
 * - Allows for template interpolation
 */
export const css = (strings: TemplateStringsArray, ...values: (string | number)[]): string => {
  const cssString = strings.reduce((result, str, i) => {
    return result + str + (values[i] || '');
  }, '');

  // Remove extra whitespace and newlines
  const cleaned = cssString
    .replace(/\s+/g, ' ')
    .replace(/{\s+/g, '{')
    .replace(/}\s+/g, '}')
    .replace(/;\s+/g, ';')
    .trim();

  if (process.env.NODE_ENV === 'development') {
    // Basic CSS validation
    try {
      new CSSStyleSheet().replaceSync(cleaned);
    } catch (e) {
      console.warn('Invalid CSS:', e);
    }
  }

  return cleaned;
};

/**
 * Tagged template literal for HTML that:
 * - Removes extra whitespace
 * - Sanitizes interpolated values
 * - Allows for template interpolation
 */
export const html = (
  strings: TemplateStringsArray,
  ...values: (string | number | { __unsafe?: boolean; value?: string })[]
): string => {
  return strings
    .reduce((result, str, i) => {
      const value = values[i];
      if (value && typeof value === 'object' && (value as { __unsafe?: boolean }).__unsafe) {
        // Insert trusted HTML/SVG as-is
        return result + str + (value as { value: string }).value;
      }
      // Otherwise, sanitize as before
      const sanitized =
        typeof value === 'string'
          ? value.replace(
              /[&<>"']/g,
              (char) =>
                ({
                  '&': '&amp;',
                  '<': '&lt;',
                  '>': '&gt;',
                  '"': '&quot;',
                  "'": '&#39;',
                }[char] ?? char),
            )
          : value ?? '';
      return result + str + sanitized;
    }, '')
    .replace(/>\s+</g, '><')
    .trim();
};

export const unsafeHTML = (raw: string): { __unsafe: boolean; value: string } => {
  // Just a marker for trusted HTML/SVG, returns as-is for direct assignment
  return { __unsafe: true, value: raw };
};
